import React, { useEffect, useMemo, useState } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { Tooltip, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InboxIcon from "@mui/icons-material/Inbox";
import LinkIcon from "@mui/icons-material/Link";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../examples/Footer";
import { appConfirm } from "components/AppDialog";
import { RemoveRowButton } from "components/RowActions";

import { ListLink, CrmRowActions, ListIconButton, ListStatus, ListText, CrmPage, CrmPageHeader, CrmActionButton, CrmNotice, CrmTableCard, LoadTypeTabs } from "../crm/shared/CrmUi";
import FlightOutlined from "@mui/icons-material/FlightOutlined";
import FlightTakeoffOutlined from "@mui/icons-material/FlightTakeoffOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
function AirShipmentTable() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [loadFilter, setLoadFilter] = useState("all");
    const [selectedRowsLCL, setSelectedRowsLCL] = useState([]);
    const [selectedRowsFCL, setSelectedRowsFCL] = useState([]);
    const [notification, setNotification] = useState({
        value: false,
        text: "",
    });
    const [selectedPlannedRouteCountries, setSelectedPlannedRouteCountries] =
        useState([]);

    const navigate = useNavigate();
    const MAX_CBM = 90;

    const selectedRowsLCLMemo = useMemo(() => {
        const map = new Map();
        (selectedRowsLCL || []).forEach((s) => map.set(String(s.id), s));
        return Array.from(map.values());
    }, [selectedRowsLCL]);

    const selectedRowsFCLMemo = useMemo(() => {
        const map = new Map();
        (selectedRowsFCL || []).forEach((s) => map.set(String(s.id), s));
        return Array.from(map.values());
    }, [selectedRowsFCL]);

    const allSelectedRows = useMemo(() => {
        const map = new Map();

        [...selectedRowsFCLMemo, ...selectedRowsLCLMemo].forEach((shipment) => {
            map.set(String(shipment.id), shipment);
        });

        return Array.from(map.values());
    }, [selectedRowsFCLMemo, selectedRowsLCLMemo]);

    const selectedTotalCbmLCL = useMemo(
        () =>
            selectedRowsLCLMemo.reduce(
                (sum, s) => sum + Number(s.attributes?.totals?.totalCbm || 0),
                0
            ),
        [selectedRowsLCLMemo]
    );

    const showNotification = (text) => {
        setNotification({ value: true, text });
        setTimeout(() => setNotification({ value: false, text: "" }), 5000);
    };

    const renderText = (value, fallback = "-") => {
        if (value === null || value === undefined || value === "") return fallback;

        if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
        ) {
            return String(value);
        }

        if (Array.isArray(value)) {
            const text = value
                .map((item) => renderText(item, ""))
                .filter(Boolean)
                .join(", ");

            return text || fallback;
        }

        if (typeof value === "object") {
            return (
                value.name ||
                value.label ||
                value.companyName ||
                value.title ||
                value.value ||
                value.id ||
                value._id ||
                fallback
            );
        }

        return fallback;
    };

    const getBlType = (shipment) => {
        const attr = shipment?.attributes || {};

        return attr.billOfLadingType || "HMAWB";
    };

    const isDirectBLShipment = (shipment) =>
        getBlType(shipment) === "DIRECT_BL";

    const formatNumber = (value) => {
        const n = Number(value || 0);
        return Number.isFinite(n) ? n.toFixed(4) : "0.0000";
    };

    const formatMoney = (amount, currency = "") => {
        const n = Number(amount || 0);

        return `${Number.isFinite(n) ? n.toFixed(2) : "0.00"}${
            currency ? ` ${renderText(currency, "")}` : ""
        }`;
    };

    const getCustomerText = (shipment) => {
        const customer = renderText(
            shipment?.attributes?.customer,
            "No Customer Data"
        );

        return customer.length > 38 ? `${customer.substring(0, 36)}...` : customer;
    };

    const chipSx = {
        fontWeight: 700,
        borderRadius: "999px",
        minWidth: 70,
        height: 28,
    };

    const renderBlTypeChip = (type) => (
        <ListStatus label={type} tone={type === "MAWB" ? "green" : type === "DIRECT_BL" ? "amber" : "blue"} />
    );
    const renderTransportChip = (type) => (
        <ListStatus label={type || "-"} tone={type?.includes("FCL") ? "green" : "purple"} />
    );

    const renderVolumeChip = (value) => (
        <ListText primary={`${formatNumber(value)} CBM`} strong />
    );

    const renderMoneyChip = (value) => (
        <ListText primary={value} strong />
    );

    const fetchAirShipments = async () => {
        try {
            setLoading(true);
            const response = await CrudService.getAirShipments();
            setData(response?.data || []);
        } catch (error) {
            console.error("Error fetching air shipments:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAirShipments();
    }, []);

    const mergePlannedRouteCountries = (rows) => {
        const allCountries = rows.flatMap(
            (shipment) =>
                shipment.attributes?.carbonFootprint?.plannedRouteCountries || []
        );

        return [...new Set(allCountries)];
    };

    const clickAddHandler = () => {
        navigate("/shipping-order/create-air-shipment/details");
    };

    const clickEditHandler = (id, shipment = null) => {
        if (
            shipment?.attributes?.billOfLadingType === "DIRECT_BL"
        ) {
            navigate(`/shipping-order/create-air-shipment/details?id=${id}&openDmbl=1`);
            return;
        }

        navigate(`/shipping-order/create-air-shipment/details?id=${id}`);
    };

    const clickDeleteHandler = async (_e, id) => {
        try {
            if (!await appConfirm("Are you sure you want to delete this air shipment?"))
                return;

            await CrudService.deleteAirShipment(id);
            await fetchAirShipments();

            showNotification("Air shipment deleted successfully");
        } catch (error) {
            console.error("Error deleting air shipment:", error);
            showNotification("Failed to delete air shipment");
        }
    };

    const handleAddRowLCL = (shipment) => {
        if (isDirectBLShipment(shipment)) {
            showNotification("DIRECT BL shipments cannot be connected to MAWB.");
            return;
        }

        const shipmentTotals = shipment.attributes?.totals || {};

        const currentTotalCbm = selectedRowsLCL.reduce(
            (sum, s) => sum + Number(s.attributes?.totals?.totalCbm || 0),
            0
        );

        const newCBM = currentTotalCbm + Number(shipmentTotals.totalCbm || 0);

        if (newCBM > MAX_CBM) {
            showNotification("Cannot add this shipment. It exceeds the maximum limits.");
            return;
        }

        if (selectedRowsLCL.some((r) => String(r.id) === String(shipment.id)))
            return;

        const next = [...selectedRowsLCL, shipment];

        setSelectedRowsLCL(next);
        setSelectedPlannedRouteCountries(mergePlannedRouteCountries(next));
    };

    const handleRemoveRowLCL = (shipment) => {
        const next = selectedRowsLCL.filter(
            (r) => String(r.id) !== String(shipment.id)
        );

        setSelectedRowsLCL(next);
        setSelectedPlannedRouteCountries(mergePlannedRouteCountries(next));
    };

    const handleAddRowFCL = (shipment) => {
        if (isDirectBLShipment(shipment)) {
            showNotification("DIRECT BL shipments cannot be connected to MAWB.");
            return;
        }

        if (selectedRowsFCL.some((r) => String(r.id) === String(shipment.id)))
            return;

        setSelectedRowsFCL((prev) => [...prev, shipment]);
    };

    const handleRemoveRowFCL = (shipment) => {
        setSelectedRowsFCL((prev) =>
            prev.filter((r) => String(r.id) !== String(shipment.id))
        );
    };

    const handleRemoveSelectedRow = (shipment) => {
        const transportType = shipment?.attributes?.airTransportType || "";

        if (transportType.includes("LCL")) {
            handleRemoveRowLCL(shipment);
            return;
        }

        handleRemoveRowFCL(shipment);
    };

    const handleConnectAMBL = () => {
        const hblRows = allSelectedRows.filter((s) => !isDirectBLShipment(s));

        if (!hblRows.length) {
            showNotification("DIRECT BL shipments cannot be connected to MAWB.");
            return;
        }

        navigate("/air-shipping-order/connect-ambl", {
            state: {
                selectedShipments: hblRows,
            },
        });
    };

    // A AHFL already on a AMBL (backend sends it as `linkedAmbl`) cannot be connected again.
    const linkedMasterMessage = (shipment) => {
        const linked = shipment?.attributes?.linkedAmbl;
        return linked ? `This shipment is already connected to ${linked.amblNumber || "a AMBL"}.` : "";
    };

    const connectBlockedMessage = (rows) =>
        (rows || []).map(linkedMasterMessage).find(Boolean) || "";

    const handleAddShipmentByTransportType = (shipment) => {
        if (linkedMasterMessage(shipment)) {
            showNotification(linkedMasterMessage(shipment));
            return;
        }

        const transportType = shipment?.attributes?.airTransportType || "";

        if (transportType.includes("LCL")) {
            handleAddRowLCL(shipment);
            return;
        }

        handleAddRowFCL(shipment);
    };

    const getRows = (dataList) => {
        const rows = [];

        (dataList || []).forEach((shipment) => {
            const attr = shipment.attributes || {};
            const shipmentTotals = attr.totals || {};

            const finalRate = attr.finalSellingRate ?? attr.sellingRate ?? 0;
            const sellingRateCurrency = formatMoney(
                finalRate,
                attr.sellingRateCurrency
            );

            const transportType = attr.airTransportType || "";

            const blType = getBlType(shipment);
            const isDBL = isDirectBLShipment(shipment);

            const linkedMessage = linkedMasterMessage(shipment);

            const alreadySelected =
                selectedRowsLCL.some((r) => String(r.id) === String(shipment.id)) ||
                selectedRowsFCL.some((r) => String(r.id) === String(shipment.id));

            rows.push({
                id: shipment.id,
                // Yük tipi filtresi için ham değer (tabloda gösterilmez)
                loadType: transportType.includes("LCL") ? "LCL" : "FCL",

                shipmentNo: (
                    <ListLink onClick={() => clickEditHandler(shipment.id, shipment)}>
                        {`AHFL${attr.shipmentNo || 0}`}
                    </ListLink>
                ),

                customer: (
                    <Tooltip title={renderText(attr.customer, "No Customer Data")} arrow>
            <span
                style={{
                    display: "inline-block",
                    maxWidth: 230,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#101828",
                }}
            >
              {getCustomerText(shipment)}
            </span>
                    </Tooltip>
                ),

                billOfLadingType: renderBlTypeChip(blType),
                airTransportType: renderTransportChip(transportType),
                totalVolume: renderVolumeChip(shipmentTotals.totalCbm),
                sellingRate: renderMoneyChip(sellingRateCurrency),

                actions: (
                    <CrmRowActions onEdit={() => clickEditHandler(shipment.id, shipment)} editTitle="Edit Air Shipment" onDelete={(e) => clickDeleteHandler(e, shipment.id)} deleteTitle="Delete Air Shipment">{!isDBL && <ListIconButton title={linkedMessage || (alreadySelected ? "Already Selected" : "Add Shipment")} onClick={() => handleAddShipmentByTransportType(shipment)} disabled={alreadySelected || Boolean(linkedMessage)} tone="green"><AddIcon /></ListIconButton>}{isDBL && <ListIconButton title="DBL shipments cannot be connected to AMBL" disabled={true} tone="green"><AddIcon /></ListIconButton>}</CrmRowActions>
                ),
            });
        });

        return rows;
    };

    useEffect(() => {
        setTableData(getRows(data));
    }, [data, selectedRowsLCL, selectedRowsFCL]);

    const selectedTableColumns = [
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Shipment No</span>
            ),
            accessor: "shipmentNo",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>
            ),
            accessor: "customer",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>BL Type</span>
            ),
            accessor: "billOfLadingType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Air Transport Type
        </span>
            ),
            accessor: "airTransportType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Selling Rate</span>
            ),
            accessor: "sellingRateCurrency",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
            ),
            accessor: "actions",
            disableSortBy: true,
        },
    ];

    const getSelectedRowsTable = (selectedData) =>
        (selectedData || []).map((shipment) => {
            const attr = shipment.attributes || {};
            const shipmentTotals = attr.totals || {};

            const finalRate = attr.finalSellingRate ?? attr.sellingRate ?? 0;
            const sellingRateCurrency = formatMoney(
                finalRate,
                attr.sellingRateCurrency
            );

            return {
                id: shipment.id,

                shipmentNo: (
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        color="info"
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        {`AHFL${attr.shipmentNo || 0}`}
                    </MDTypography>
                ),

                customer: (
                    <Tooltip title={renderText(attr.customer, "Unknown Customer")} arrow>
            <span
                style={{
                    display: "inline-block",
                    maxWidth: 230,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#101828",
                }}
            >
              {getCustomerText(shipment)}
            </span>
                    </Tooltip>
                ),

                billOfLadingType: renderBlTypeChip(getBlType(shipment)),
                airTransportType: renderTransportChip(attr.airTransportType || ""),
                totalVolume: renderVolumeChip(shipmentTotals.totalCbm),
                sellingRateCurrency: renderMoneyChip(sellingRateCurrency),

                actions: (
                    <MDBox display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="Remove Shipment">
                            <RemoveRowButton onClick={() => handleRemoveSelectedRow(shipment)} tooltip="Remove row" />
                        </Tooltip>
                    </MDBox>
                ),
            };
        });

    const selectedDataTableData = {
        columns: selectedTableColumns,
        rows: getSelectedRowsTable(allSelectedRows),
    };

    const dataTableColumns = [
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Shipment No</span>
            ),
            accessor: "shipmentNo",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Customer</span>
            ),
            accessor: "customer",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>BL Type</span>
            ),
            accessor: "billOfLadingType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>
          Air Transport Type
        </span>
            ),
            accessor: "airTransportType",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Selling Rate</span>
            ),
            accessor: "sellingRate",
        },
        {
            Header: () => (
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
            ),
            accessor: "actions",
            disableSortBy: true,
        },
    ];

    const EmptyState = ({ label }) => (
        <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            gap={1}
        >
            <InboxIcon sx={{ fontSize: 42, color: "text.disabled" }} />
            <MDTypography variant="button" color="secondary" fontWeight="regular">
                {label}
            </MDTypography>
        </MDBox>
    );

    const CapacityGauge = ({ label, value, max, unit, color }) => {
        const pct = Math.min(100, Math.round((value / max) * 100));
        return (
            <MDBox flex={1} minWidth={180}>
                <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                    <MDTypography variant="caption" fontWeight="medium" color="text">
                        {label}
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="bold" color={color}>
                        {value.toFixed(2)} / {max} {unit}
                    </MDTypography>
                </MDBox>
                <MDProgress
                    variant="gradient"
                    color={pct >= 100 ? "error" : pct >= 80 ? "warning" : color}
                    value={pct}
                />
            </MDBox>
        );
    };

    const tableWrapperSx = {
        p: 2,
        borderRadius: "18px",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid #e5e7eb",
        boxShadow: "0 12px 32px rgba(15,23,42,0.08)",
        overflow: "hidden",
    };

    const visibleAirRows = loadFilter === "all" ? tableData : tableData.filter((row) => row.loadType === loadFilter);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <CrmPage>
                <CrmPageHeader
                    title="Air Shipments"
                    subtitle={`${tableData.length} air shipments (HAWB and direct). Open a shipment number to edit it, or add shipments to an AMBL.`}
                    actions={
                        <CrmActionButton icon={<AddIcon />} onClick={clickAddHandler}>
                            New Air Shipment
                        </CrmActionButton>
                    }
                />

                {notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}

                <LoadTypeTabs
                    options={[
                        { value: "all", label: "All shipments", caption: "Every air shipment", count: tableData.length, icon: <FlightOutlined /> },
                        { value: "FCL", label: "FCL", caption: "Full load", count: tableData.filter((r) => r.loadType === "FCL").length, icon: <FlightTakeoffOutlined /> },
                        { value: "LCL", label: "LCL", caption: "Consolidated load", count: tableData.filter((r) => r.loadType === "LCL").length, icon: <Inventory2Outlined /> },
                    ]}
                    value={loadFilter}
                    onChange={setLoadFilter}
                />

                <CrmTableCard
                    title={loadFilter === "all" ? "Air shipment list" : `${loadFilter} air shipments`}
                    summary={`${visibleAirRows.length} shipments`}
                >
                    {loading ? (
                        <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
                            <CircularProgress size={32} />
                        </MDBox>
                    ) : visibleAirRows.length > 0 ? (
                        <DataTable
                            table={{
                                columns: dataTableColumns,
                                rows: visibleAirRows,
                            }}
                            canSearch
                        />
                    ) : (
                        <EmptyState label="No air shipments yet." />
                    )}
                </CrmTableCard>

                {allSelectedRows.length > 0 && (
                    <CrmTableCard title="Selected shipments" summary={`${allSelectedRows.length} selected · ready to connect to an AMBL`}>
                        {selectedRowsLCLMemo.length > 0 && (
                            <MDBox px={2.5} pt={2}>
                                <CapacityGauge
                                    label="LCL Consolidation CBM"
                                    value={selectedTotalCbmLCL}
                                    max={MAX_CBM}
                                    unit="m³"
                                    color="info"
                                />
                            </MDBox>
                        )}
                        <DataTable table={selectedDataTableData} canSearch />
                        <MDBox display="flex" justifyContent="flex-end" p={2.5} pt={0}>
                            <CrmActionButton
                                icon={<LinkIcon />}
                                onClick={handleConnectAMBL}
                                disabled={Boolean(connectBlockedMessage(allSelectedRows))}
                                title={connectBlockedMessage(allSelectedRows) || undefined}
                            >
                                Connect AMBL
                            </CrmActionButton>
                        </MDBox>
                    </CrmTableCard>
                )}
            </CrmPage>

            <Footer />
        </DashboardLayout>
    );
}

export default AirShipmentTable;